<script lang="ts" setup>
  import type { ButtonHTMLAttributes } from 'vue'

  type Props = {
    type?: string
    disabled?: boolean
    buttonType?: ButtonHTMLAttributes['type']
  }

  const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    type: 'primary',
  })
  const isClicking = ref(false)

  const effectStyle = ref({
    transform: 'translate(0, 0)',
  })

  function onMouseUp() {
    setTimeout(() => {
      effectStyle.value = {
        transform: 'translate(0, 0)',
      }
      isClicking.value = false
    }, 333)
  }

  function onMouseDown(e: MouseEvent) {
    effectStyle.value = {
      transform: `translate(${e.offsetX - (e.currentTarget as HTMLElement).clientWidth / 2}px, ${
        e.offsetY - (e.currentTarget as HTMLElement).clientHeight / 2
      }px)`,
    }
    isClicking.value = true
  }

</script>

<template>
  <button
    class="c-button"
    :class="type"
    :disabled="!!props.disabled"
    :type="props.buttonType"
  >
    <span class="slot-content">
      <!-- @slot Button text content -->
      <slot />
    </span>
    <span
      class="effect"
      :class="{ click: isClicking }"
      @mousedown="onMouseDown"
      @mouseup="onMouseUp"
    ><span
      class="inner"
      :style="effectStyle"
    /></span>
  </button>
</template>

<style scoped>

.c-button {
  position: relative;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5em;
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  font-family: Bariol, sans-serif;
  font-size: inherit;
  font-size: 1em;
  font-weight: bold;
  letter-spacing: inherit;
  transition: all 0.5s ease;
  user-select: none;
  white-space: nowrap;

  .slot-content {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.125rem;
    line-height: 1em;
  }

  &.primary {
    background-color: var(--color-primary);
  }

  &.info {
    background-color: var(--color-error);
  }

  &.secondary {
    background-color: var(--color-secondary);

    &.negative {
      background-color: white;

      .slot-content {
        color: var(--color-secondary);
      }
    }
  }

  &.tertiary {
    padding: 0.25em 0.5em;
    border: solid 1px var(--color-secondary);
    background-color: white;

    .slot-content {
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1em;
      font-weight: 600;
    }
  }

  &::after {
    background-color: rgb(255 255 255);
    opacity: 0;
  }

  &:disabled::after {
    position: absolute;
    border-radius: 4px;
    background-color: rgb(255 255 255);
    content: "";
    cursor: default;
    inset: 0;
    opacity: 0.3;
  }

  .effect {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    inset: 0;

    &.click {
      .inner {
        position: relative;
        display: flex;
        overflow: hidden;
        border-radius: 1000em;
        margin: auto;
        animation: 0.33s click ease;
        background-color: var(--color-secondary-light);
        content: "";
        opacity: 0;

        @keyframes click {
          from {
            width: 0.5em;
            height: 0.5em;
            opacity: 0.3;
          }

          to {
            width: 2em;
            height: 2em;
            opacity: 0;
          }
        }
      }
    }
  }
}
</style>
